import { SubscriptionOptions } from '@reduxjs/toolkit/dist/query/core/apiState';
import { UseQuery } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { useSafeContext } from 'hooks/context.hooks';
import { merge, omit } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams as rrUseSearchParams } from 'react-router-dom';
import { selectUserDetails } from 'redux/user/user.selectors';
import { Option } from 'util/multi-select';
import {
  FilterTimeZone,
  SearchParam,
  SearchParamsContext,
  SearchParamsContextType,
  SearchQueryContext,
  SearchQueryContextBaseType,
  SetParamFn,
  SetParamValueFn,
} from './search.context';
import { gatedLocationsToQueryString } from './search.utils';

export type Paging = {
  count: number;
  currentPage: number;
  total: number;
  totalPages: number;
};

export type SearchOptions = {
  getPaging?: (data: any) => Paging;
  getSearchResults?: (data: any) => any[];
  useUrl?: boolean;
  queryOptions?: SubscriptionOptions;
  getIsSkipToken?: (data: any) => boolean;
};

export type SearchParamsOptions = {
  useUrl?: boolean;
};

export type SearchQueryOptions = {
  getPaging?: (data: any) => Paging;
  getSearchResults?: (data: any) => any[];
  queryOptions?: SubscriptionOptions;
  getIsSkipToken?: (data: any) => boolean;
  filteredSearchParams?: string[];
  extraParams?: Record<string, string | number | boolean | null | undefined>;
};

export type UseSearchFn = (
  useQuery: UseQuery<any>,
  initialSearchParams: SearchParam[],
  options?: SearchOptions,
) => { params: SearchParamsContextType; query: Parameters<UseSearchQueryFn> };

export type UseSearchParamsFn = (
  initialSearchParams: SearchParam[],
  options?: SearchParamsOptions,
) => SearchParamsContextType;

export type UseSearchQueryFn = (
  useQuery: UseQuery<any>,
  options?: SearchQueryOptions,
) => SearchQueryContextBaseType;

const defaultParamsOptions: SearchParamsOptions = {
  useUrl: true,
};

const defaultQueryOptions: SearchQueryOptions = {
  getPaging: data => data?.paging,
  getSearchResults: data =>
    Object.entries(data).find(([key]) => key !== 'paging')?.[1] as any[],
};

const makeUrlParams = (searchParams: { [key: string]: SearchParam }) =>
  Object.entries(searchParams).reduce((acc, [paramName, searchParam]) => {
    if (searchParam.excludeFromUrl) return acc;
    const value = searchParam.toUrlString(searchParam.value);
    if (!!value) acc[paramName] = value;
    return acc;
  }, {});

// True when the computed url params already match what is in the url, so
// writing them again would be a redundant history `replace`.
const urlParamsMatch = (
  next: { [key: string]: string },
  current: URLSearchParams,
) => {
  const nextKeys = Object.keys(next);
  if (nextKeys.length !== Array.from(current.keys()).length) return false;
  return nextKeys.every(key => current.get(key) === next[key]);
};

export const makeQueryParam = (
  searchParam: SearchParam,
  searchParams: { [key: string]: SearchParam },
) =>
  searchParam.toQueryString?.(searchParam.value, searchParams) ??
  searchParam.toUrlString(searchParam.value);

const makeQueryParams = (searchParams: { [key: string]: SearchParam }) =>
  Object.entries(searchParams).reduce((acc, [paramName, searchParam]) => {
    if (searchParam.excludeFromQuery) return acc;

    const value = makeQueryParam(searchParam, searchParams);
    if (!!value) acc[paramName] = value;
    return acc;
  }, {});

const makeSearchParamsValues = (searchParams: { [key: string]: SearchParam }) =>
  Object.entries(searchParams).reduce((acc, [paramName, searchParam]) => {
    acc[paramName] = makeQueryParam(searchParam, searchParams);
    return acc;
  }, {});

export const useSearch: UseSearchFn = (
  useQuery,
  initialSearchParams,
  optionsArg,
) => ({
  params: useSearchParams(initialSearchParams, optionsArg),
  query: [useQuery, optionsArg],
});

export const useSearchParams: UseSearchParamsFn = (
  initialSearchParamsArg,
  optionsArg = {},
) => {
  const { current: initialSearchParams } = useRef(initialSearchParamsArg);
  const { current: options } = useRef(
    merge({}, defaultParamsOptions, optionsArg),
  );

  const [urlSearchParams, setUrlSearchParams] = rrUseSearchParams();
  const [searchParams, setSearchParams] = useState(
    initialSearchParams.reduce<{ [key: string]: SearchParam }>(
      (acc, initialSearchParam) => {
        const searchParam = { ...initialSearchParam };
        acc[searchParam.paramName] = searchParam;
        // params initially in the url override defaults
        if (!!urlSearchParams.get(searchParam.paramName)) {
          searchParam.value = searchParam.parse(
            urlSearchParams.get(searchParam.paramName),
          );
        }
        return acc;
      },
      {},
    ),
  );
  const searchParamsValuesRef = useRef(makeSearchParamsValues(searchParams));

  const setAsyncSearchParamsOptions = async (
    searchParam,
    searchParamsValues,
  ) => {
    const options = await searchParam.asyncOptions(searchParamsValues);
    setSearchParams(currentSearchParams => ({
      ...currentSearchParams,
      [searchParam.paramName]: {
        ...currentSearchParams[searchParam.paramName],
        options,
      },
    }));
  };

  useEffect(() => {
    initialSearchParams.forEach(async searchParam => {
      if (!!searchParam.asyncOptions) {
        setAsyncSearchParamsOptions(searchParam, searchParamsValuesRef.current);
      }
    });
  }, [initialSearchParams]);

  useEffect(() => {
    const newSearchParamsValues = makeSearchParamsValues(searchParams);

    Object.entries(searchParams).forEach(([paramName, searchParam]) => {
      if (
        searchParam.asyncDependents &&
        searchParam.value !== searchParamsValuesRef.current[paramName]
      ) {
        searchParam.asyncDependents.forEach(dependentParamName => {
          const dependentSearchParam = searchParams[dependentParamName];
          setAsyncSearchParamsOptions(
            dependentSearchParam,
            newSearchParamsValues,
          );
        });
      }
    });

    searchParamsValuesRef.current = newSearchParamsValues;
  }, [searchParams]);

  // Set url params to match search params whenever it changes
  useEffect(() => {
    if (!options.useUrl) return;
    const nextUrlParams = makeUrlParams(searchParams);
    if (urlParamsMatch(nextUrlParams, urlSearchParams)) return;
    setUrlSearchParams(nextUrlParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const setParam: SetParamFn = (paramName, value) => {
    setSearchParams(currentSearchParams => ({
      ...currentSearchParams,
      [paramName]: {
        ...currentSearchParams[paramName],
        ...value,
      },
    }));
  };

  const setParamValue: SetParamValueFn = (paramName, value) => {
    setSearchParams(currentSearchParams => ({
      ...currentSearchParams,
      [paramName]: {
        ...currentSearchParams[paramName],
        value,
      },
    }));
  };

  const setPaginationModel = paginationModel => {
    setSearchParams(currentSearchParams => ({
      ...currentSearchParams,
      page: {
        ...currentSearchParams.page,
        value: paginationModel.page,
      },
      count: {
        ...currentSearchParams.count,
        value: paginationModel.pageSize,
      },
    }));
  };

  const removeParams = (paramNames: string[]) => {
    setSearchParams(currentSearchParams =>
      Object.entries(currentSearchParams).reduce(
        (acc, [paramName, searchParam]) => {
          if (paramNames.includes(paramName)) {
            searchParam.value = undefined;
          }
          acc[paramName] = searchParam;
          return acc;
        },
        {},
      ),
    );
  };

  const resetParam = paramName => {
    setSearchParams(currentSearchParams => ({
      ...currentSearchParams,
      [paramName]: {
        ...currentSearchParams[paramName],
        value: currentSearchParams[paramName].defaultValue,
      },
    }));
  };

  const resetAllParams = () => {
    setSearchParams(currentSearchParams => {
      return Object.entries(currentSearchParams).reduce(
        (acc, [paramName, searchParam]) => {
          acc[paramName] = {
            ...searchParam,
            value: searchParam.defaultValue,
          };
          return acc;
        },
        {},
      );
    });
  };

  const setParamFocus = (paramName, focus) => {
    setSearchParams(currentSearchParams => ({
      ...currentSearchParams,
      [paramName]: {
        ...currentSearchParams[paramName],
        filter: {
          ...currentSearchParams[paramName].filter,
          focus,
        },
      },
    }));
  };

  const searchFilters = useMemo(
    () =>
      Object.entries(searchParams).reduce((acc, [paramName, searchParam]) => {
        if (!!searchParam.filter) acc[paramName] = searchParam;
        return acc;
      }, {}),
    [searchParams],
  );

  const categories = useMemo(
    () =>
      Object.entries(searchParams).reduce((acc, [paramName, searchParam]) => {
        if (!!searchParam.category) acc[paramName] = searchParam;
        return acc;
      }, {}),
    [searchParams],
  );

  const queryParam = useMemo(
    () => Object.values(searchParams).find(searchParam => searchParam.query),
    [searchParams],
  );

  return {
    setParam,
    setParamValue,
    setPaginationModel,
    removeParams,
    resetParam,
    resetAllParams,
    setParamFocus,
    searchParams,
    searchFilters,
    categories,
    queryParam,
  };
};

export function useDebounceQueryParams<T>(value: T): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    if (JSON.stringify(value) === JSON.stringify(debouncedValue)) return;

    const handler = setTimeout(() => setDebouncedValue(value), 300);
    return () => clearTimeout(handler);
  }, [debouncedValue, value]);

  return debouncedValue;
}

export const useSearchQuery: UseSearchQueryFn = (useQuery, optionsArg) => {
  const { current: options } = useRef(
    merge({}, defaultQueryOptions, optionsArg),
  );

  const { filteredSearchParams: filteredSP = [], extraParams = {} } =
    optionsArg ?? {};

  const { searchParams } = useSearchParamsContext();
  const filteredSearchParams = omit(searchParams, filteredSP);
  const debouncedQueryParams = useDebounceQueryParams({
    ...makeQueryParams(filteredSearchParams),
    ...extraParams,
  });

  const { data = {}, ...queryState } = useQuery(debouncedQueryParams, {
    ...options.queryOptions,
    skip: options.getIsSkipToken?.(filteredSearchParams) ?? false,
  });

  return {
    paging: options.getPaging(data),
    searchResults: options.getSearchResults(data),
    data,
    queryState,
  };
};

export const useSearchParamsContext = () => useSafeContext(SearchParamsContext);

export const useSearchQueryContext = (queryName?: string) => {
  const queries = useSearchQueryContexts();
  return queryName ? queries[queryName] : Object.values(queries)[0];
};

export const useSearchQueryContexts = () =>
  useSafeContext(SearchQueryContext).queries;

type UseSearchParamReturn<T = unknown> = {
  value: T;
  setValue: (value: T) => void;
  options: Option[];
  searchParam: SearchParam<T>;
  reset: () => void;
  setFocus: (focus: () => void) => void;
  timeZone?: FilterTimeZone;
};

export const useSearchParam = <T = unknown>(
  paramName,
): UseSearchParamReturn<T> => {
  const context = useSearchParamsContext();
  const searchParam = context.searchParams?.[paramName] as SearchParam<T>;

  return searchParam ? makeSearchParam(searchParam, context) : null;
};

const makeSearchParam = <T = unknown>(
  searchParam: SearchParam,
  context: SearchParamsContextType,
) => ({
  value: searchParam.value as T,
  setValue: value => context.setParamValue(searchParam.paramName, value),
  options: searchParam.options,
  searchParam: context.searchParams?.[searchParam.paramName] as SearchParam<T>,
  reset: () => context.resetParam(searchParam.paramName),
  setFocus: focus => context.setParamFocus(searchParam.paramName, focus),
  timeZone: searchParam.timeZone,
});

export const useCategorySearchParam = <T = unknown>(
  paramName?: string,
): UseSearchParamReturn => {
  const context = useSearchParamsContext();

  let searchParam;
  if (paramName) {
    searchParam = context.searchParams?.[paramName] as SearchParam<T>;
  } else {
    searchParam =
      Object.values(context.searchParams)?.find(sp => sp.query && sp.value) ??
      Object.values(context.searchParams)?.find(sp => sp.query);
  }

  return makeSearchParam(searchParam, context);
};

export const useLocationsToQueryString = (all: string = '*') => {
  const admin = useSelector(selectUserDetails);

  return useCallback(
    (locations: string[]) => gatedLocationsToQueryString(locations, admin, all),
    [admin, all],
  );
};
